// frontend/src/pages/Result.jsx
// ✅ UPDATED: Pi SDK native download via openUrlInSystemBrowser
// ✅ NEW: Veo animation feature (image → video)
// ✅ NEW: Download video button matches image download styling

import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { STYLE_DEFS } from "../shared/styles.js";
import { getIdentityHeaders } from "../lib/identity";
import { trackStyleView } from "../utils/trackStyleView.js";
import { 
  API_BASE, 
  fromNow,
  toast 
} from "../lib/api";

const api = (path) => API_BASE ? `${API_BASE}${path}` : path;

/* --------------------------- helpers / normalize -------------------------- */
function normKey(s) {
  return String(s?.key ?? s?.slug ?? "").trim().toLowerCase();
}

const STYLES = STYLE_DEFS.filter((s) => !s.hidden).map((s) => ({
  key: normKey(s),
  name: s?.name || "",
  status: s?.status || "ready",
}));

/* -------------------------------- page --------------------------------- */
export default function Result() {
  const { jobId } = useParams();
  const nav = useNavigate();

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [promptOpen, setPromptOpen] = React.useState(false);

  // inline editor state
  const [showEditor, setShowEditor] = React.useState(false);
  const [editPrompt, setEditPrompt] = React.useState("");
  const [editStyle, setEditStyle] = React.useState("");
  const [editAspect, setEditAspect] = React.useState("1:1");
  const [submitting, setSubmitting] = React.useState(false);

  // animation state
  const [pricing, setPricing] = React.useState(null);
  const [animateState, setAnimateState] = React.useState({ status: "idle" });
  const [motionPrompt, setMotionPrompt] = React.useState("");

  React.useEffect(() => {
    if (data && data.style) {
      const styleSlug = String(data.style || "").toLowerCase().trim();
      if (styleSlug && styleSlug !== "default") {
        trackStyleView(styleSlug, "direct");
      }
    }
  }, [data]);

  // fetch animation pricing on mount
  React.useEffect(() => {
    fetch(api("/api/animate/pricing"))
      .then(r => r.json())
      .then(setPricing)
      .catch(() => setPricing({ usd: 1.5, credits: 9, durationSeconds: 3 }));
  }, []);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        // Reset per-generation animation state so a previous image's video or
        // in-flight job doesn't bleed into this one after edit/navigation.
        setAnimateState({ status: "idle" });
        setMotionPrompt("");
        const res = await fetch(api(`/api/generations/${jobId}`), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        const j = await res.json();
        if (!res.ok) throw new Error(j?.error || res.statusText);
        if (alive) {
          setData(j);
          setEditPrompt(j?.prompt || "");
          setEditStyle(j?.style || "");
          setEditAspect(j?.aspectRatio || "1:1");

          // ✅ NEW: If this generation already has a video, seed completed state
          if (j?.videoUrl) {
            setAnimateState({
              status: "complete",
              jobId: null, // not from a current job — already persisted
              videoUrl: j.videoUrl,
              fromPersisted: true,
            });
          }
        }
      } catch (e) {
        if (alive) setErr(e.message || "Failed to load result");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [jobId]);

  const imageUrl = data?.id ? api(`/api/generations/${data.id}/download`) : null;
  const downloadUrl = data?.id ? api(`/api/generations/${data.id}/download?download=1`) : null;

  const onDownload = React.useCallback(async () => {
    if (!imageUrl) return;
    
    if (window.Pi && typeof window.Pi.openUrlInSystemBrowser === 'function') {
      try {
        await window.Pi.openUrlInSystemBrowser(downloadUrl);
        toast("Opening in system browser for download");
      } catch (err) {
        console.error("Pi SDK download error:", err);
        if (err.message === "No minimal requirements") {
          toast("Please update Pi Browser to download images");
        } else if (err.message === "Failed to open URL") {
          toast("Invalid image URL");
        } else {
          fallbackDownload();
        }
      }
    } else {
      fallbackDownload();
    }
    
    async function fallbackDownload() {
      try {
        const res = await fetch(api(`/api/generations/${jobId}/download?download=1`), {
          method: "GET",
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `artibytes-${jobId}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast("Downloaded!");
      } catch (err) {
        toast(err.message || "Download failed");
      }
    }
  }, [imageUrl, downloadUrl, jobId]);

  // ✅ NEW: download the animation video
  // Signed URLs expire after 1h, so we re-fetch the freshest URL before download.
  const onDownloadVideo = React.useCallback(async () => {
    try {
      let freshVideoUrl = animateState.videoUrl;

      // If we have a jobId (from a current session), poll status for fresh signed URL.
      // Otherwise fall back to the URL already in state (will work if recently loaded).
      if (animateState.jobId) {
        const r = await fetch(api(`/api/animate/status/${animateState.jobId}`), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        const j = await r.json();
        if (j?.signedVideoUrl) freshVideoUrl = j.signedVideoUrl;
      } else if (animateState.fromPersisted && data?.id) {
        // Re-fetch the generation to get a fresh signed URL
        const r = await fetch(api(`/api/generations/${data.id}`), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        const j = await r.json();
        if (j?.videoUrl) freshVideoUrl = j.videoUrl;
      }

      if (!freshVideoUrl) {
        throw new Error("Video URL unavailable");
      }

      // Pi Browser native
      if (window.Pi && typeof window.Pi.openUrlInSystemBrowser === 'function') {
        try {
          await window.Pi.openUrlInSystemBrowser(freshVideoUrl);
          toast("Opening in system browser for download");
          return;
        } catch (err) {
          console.error("Pi SDK video download error:", err);
          if (err.message === "No minimal requirements") {
            toast("Please update Pi Browser to download videos");
            return;
          }
          // fall through to blob fallback
        }
      }

      // Browser fallback: fetch and trigger blob download
      const res = await fetch(freshVideoUrl);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `artibytes-${jobId}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast("Downloaded!");
    } catch (e) {
      toast(e.message || "Video download failed");
    }
  }, [animateState, data?.id, jobId]);

  React.useEffect(() => {
    function onKey(e) {
      if (e.target && /input|textarea/i.test(e.target.tagName)) return;
      if (e.key.toLowerCase() === "d") onDownload();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDownload]);

  function openEditor() {
    if (!data) return;
    setShowEditor(true);
  }
  
  function closeEditor() {
    setShowEditor(false);
  }

  async function applyEditsAndRegenerate() {
    if (!editPrompt.trim()) {
      toast("Please enter a prompt");
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(api("/api/generations"), {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...getIdentityHeaders() 
        },
        body: JSON.stringify({
          prompt: editPrompt.trim(),
          style: editStyle || undefined,
          aspectRatio: editAspect || "1:1",
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || res.statusText);
      toast("Generating...");
      nav(`/result/${j.id}`);
      setShowEditor(false);
    } catch (e) {
      toast(e.message || "Failed to regenerate");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAnimate() {
    if (!data?.id || !pricing) return;
    setAnimateState({ status: "processing" });
    try {
      const res = await fetch(api("/api/animate"), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getIdentityHeaders(),
        },
        body: JSON.stringify({
          generationId: data.id,
          ...(motionPrompt.trim() ? { prompt: motionPrompt.trim() } : {}),
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || `HTTP ${res.status}`);
      toast("Animation started — video in 30–120s");
      setAnimateState({ status: "processing", jobId: j.jobId });
      pollAnimation(j.jobId);
    } catch (e) {
      setAnimateState({ status: "failed", error: e.message });
      toast(e.message || "Failed to start animation");
    }
  }

  function pollAnimation(jobId) {
    const interval = setInterval(async () => {
      try {
        const r = await fetch(api(`/api/animate/status/${jobId}`), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        const j = await r.json();
        if (j.status === "complete") {
          clearInterval(interval);
          setAnimateState({
            status: "complete",
            jobId,
            videoUrl: j.signedVideoUrl,
          });
          toast("Video ready! 🎬");
        } else if (j.status === "failed") {
          clearInterval(interval);
          setAnimateState({ status: "failed", error: j.error });
          toast(`Animation failed: ${j.error}`);
        }
      } catch (e) {
        console.warn("Animate poll error:", e);
      }
    }, 10_000);
    setTimeout(() => clearInterval(interval), 360_000);
  }

  const styleId = React.useMemo(() => {
    const s = String(data?.style || "").toLowerCase().trim();
    if (!s || s === "default") return null;
    return s;
  }, [data?.style]);

  return (
    <Screen>
      <header style={{ marginBottom: 8 }}>
        <h1 className="h1" style={{ marginBottom: 6 }}>Result</h1>
        {data?.prompt && (
          <div style={{ margin: 0, color: "var(--muted)" }}>
            <div
              style={
                promptOpen
                  ? undefined
                  : {
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden",
                    }
              }
            >
              <span style={{ color: "var(--text)" }}><b>Prompt:</b></span>{" "}
              <em>{data.prompt}</em>
            </div>
            {String(data.prompt).length > 120 && (
              <button
                type="button"
                onClick={() => setPromptOpen((v) => !v)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  marginTop: 2,
                  color: "#7C3AED",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {promptOpen ? "Read less" : "Read more"}
              </button>
            )}
          </div>
        )}
      </header>

      <div
        style={{
          position: "sticky",
          top: 68,
          zIndex: 5,
          background:
            "linear-gradient(180deg, rgba(255,255,255,.95), rgba(255,255,255,.75))",
          backdropFilter: "saturate(1.2) blur(4px)",
          borderRadius: 14,
          padding: "10px 12px",
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
          border: "1px solid var(--hairline)",
          marginBottom: 14,
        }}
      >
        <button className="btn btn-primary" onClick={onDownload} disabled={!imageUrl}>
          Download
        </button>

        {/* ✅ NEW: Download video — same primary styling, same row as Download */}
        {animateState.status === "complete" && animateState.videoUrl && (
          <button className="btn btn-primary" onClick={onDownloadVideo}>
            Download video
          </button>
        )}

        <button className="btn" onClick={openEditor} disabled={!data}>
          Edit prompt & style
        </button>
        <button className="btn" onClick={() => nav("/gallery")}>
          View in gallery
        </button>

        {/* Animate button — hidden once we have a video */}
        {animateState.status === "idle" && pricing && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="text"
              value={motionPrompt}
              onChange={(e) => setMotionPrompt(e.target.value)}
              placeholder="Describe the motion (optional) — e.g. slow zoom in, hair blowing"
              maxLength={300}
              disabled={!data}
              style={{
                height: 38,
                minWidth: 240,
                flex: "1 1 240px",
                padding: "0 12px",
                borderRadius: 10,
                border: "1px solid var(--hairline)",
                fontSize: 14,
                background: "#fff",
                color: "var(--text)",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && data) handleAnimate();
              }}
            />
            <button className="btn" onClick={handleAnimate} disabled={!data}>
              🎬 Animate ({pricing.durationSeconds}s — {pricing.credits} credits)
            </button>
          </div>
        )}
        {animateState.status === "processing" && (
          <span style={{ fontSize: 13, color: "var(--muted)" }}>
            ⏳ Generating video...
          </span>
        )}

        <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--muted)" }}>
          Shortcut: <kbd>D</kbd> download
        </div>
      </div>

      {loading && <Skeleton />}
      {!loading && err && <ErrorState message={err} />}
      {!loading && !err && data && (
        <div style={{ display: "grid", gap: 16 }}>
          <Meta data={data} />
          <ImageCard src={imageUrl} alt={data.prompt} />

          {/* Animation result */}
          {animateState.status === "complete" && animateState.videoUrl && (
            <div
              style={{
                borderRadius: 24,
                overflow: "hidden",
                border: "1px solid var(--hairline)",
                boxShadow: "0 10px 25px rgba(0,0,0,.06), 0 2px 8px rgba(0,0,0,.04)",
                background: "#000",
              }}
            >
              <video
                src={animateState.videoUrl}
                controls
                autoPlay={!animateState.fromPersisted}
                loop
                playsInline
                style={{ width: "100%", display: "block" }}
              />
            </div>
          )}
          {animateState.status === "failed" && (
            <div
              style={{
                padding: 12,
                border: "1px solid #f2c2c2",
                background: "#fff7f7",
                color: "#8a1f1f",
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <span>
                Animation failed: {animateState.error}. Credits were refunded.
              </span>
              <button
                className="btn btn-xs"
                onClick={() => setAnimateState({ status: "idle" })}
              >
                Try again
              </button>
            </div>
          )}

          {styleId && (
            <StyleFeedback 
              styleId={styleId}
              generationId={data.id}
            />
          )}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Link to="/home" className="btn">
              Home
            </Link>
            <Link to="/profile" className="btn btn-primary">
              Go to Profile
            </Link>
          </div>
        </div>
      )}

      {showEditor && (
        <EditModal
          prompt={editPrompt}
          styleId={editStyle}
          aspect={editAspect}
          onPromptChange={setEditPrompt}
          onStyleChange={setEditStyle}
          onAspectChange={setEditAspect}
          onClose={closeEditor}
          onApply={applyEditsAndRegenerate}
          busy={submitting}
        />
      )}
    </Screen>
  );
}

/* ------------------------------- Pieces ---------------------------------- */

function Meta({ data }) {
  return (
    <div
      style={{
        border: "1px solid var(--hairline)",
        borderRadius: 16,
        padding: 12,
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
        alignItems: "center",
        background:
          "linear-gradient(180deg, rgba(250,250,255,.6), rgba(245,245,255,.35))",
      }}
    >
      <Chip label={`Style: ${data?.style ?? "default"}`} />
      <Chip label={`Aspect: ${data?.aspectRatio ?? "1:1"}`} />
      <Chip label={fromNow(data?.createdAt)} />
    </div>
  );
}

function Chip({ label }) {
  return (
    <span
      style={{
        fontSize: 12,
        padding: "6px 10px",
        borderRadius: 999,
        border: "1px solid var(--hairline)",
        background: "white",
      }}
    >
      {label}
    </span>
  );
}

function ImageCard({ src, alt }) {
  return (
    <div
      style={{
        borderRadius: 24,
        overflow: "hidden",
        border: "1px solid var(--hairline)",
        boxShadow: "0 10px 25px rgba(0,0,0,.06), 0 2px 8px rgba(0,0,0,.04)",
        background:
          "radial-gradient(1200px 400px at 50% -10%, rgba(128,78,255,.11), transparent 60%)",
        minHeight: 400,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt || "Result image"}
          style={{ width: "100%", display: "block", background: "#fafafa" }}
          loading="eager"
        />
      ) : (
        <div style={{ padding: 32, textAlign: "center", color: "var(--muted)" }}>
          No image URL available.
        </div>
      )}
    </div>
  );
}

/* ----------------------- Style Feedback Component ----------------------- */
function StyleFeedback({ styleId, generationId }) {
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [hasLiked, setHasLiked] = React.useState(false);
  const [liking, setLiking] = React.useState(false);
  const [reporting, setReporting] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        
        const res = await fetch(api(`/api/feedback/styles/${encodeURIComponent(styleId)}/stats`), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        
        const statsData = await res.json();
        if (!res.ok) throw new Error(statsData?.error || "Failed to fetch stats");
        
        if (alive) {
          setStats(statsData);
          setHasLiked(statsData.userHasLiked || false);
        }
      } catch (e) {
        console.warn("Failed to fetch style stats:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    
    return () => { alive = false; };
  }, [styleId]);

  const handleLike = React.useCallback(async () => {
    if (hasLiked || liking) return;
    
    try {
      setLiking(true);
      
      const res = await fetch(api(`/api/feedback/styles/${encodeURIComponent(styleId)}/like`), {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...getIdentityHeaders() 
        },
        body: JSON.stringify({ generationId }),
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to like style");
      
      setHasLiked(true);
      setStats(prev => ({
        ...prev,
        likes: (prev?.likes || 0) + 1,
        userHasLiked: true,
      }));
      
      toast("Thanks for your feedback! ❤️");
    } catch (e) {
      toast(e.message || "Failed to like style");
    } finally {
      setLiking(false);
    }
  }, [styleId, generationId, hasLiked, liking]);

  const handleReport = React.useCallback(async () => {
    const reason = window.prompt(
      "What issue did you encounter with this style?\n\n" +
      "Examples: Poor quality, doesn't match description, inappropriate content"
    );
    
    if (!reason || !reason.trim()) return;
    
    try {
      setReporting(true);
      
      const res = await fetch(api(`/api/feedback/styles/${encodeURIComponent(styleId)}/report`), {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
          ...getIdentityHeaders() 
        },
        body: JSON.stringify({ 
          generationId,
          reason: reason.trim(),
        }),
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to report issue");
      
      toast("Thank you for reporting. We'll review this.");
    } catch (e) {
      toast(e.message || "Failed to report issue");
    } finally {
      setReporting(false);
    }
  }, [styleId, generationId]);

  if (loading) {
    return (
      <div className="style-feedback">
        <div className="sf-skeleton" />
      </div>
    );
  }

  const reputation = stats?.reputation;
  const likes = stats?.likes || 0;
  const usageCount = stats?.usageCount || 0;

  return (
    <>
      <StyleFeedbackTag />
      <div className="style-feedback">
        <div className="sf-header">
          <div className="sf-title">Style Feedback</div>
          <div className="sf-style-name">{styleId}</div>
        </div>

        <div className="sf-message">
          By using this style you are supporting an <strong>independent artist</strong>
        </div>

        <div className="sf-stats-row">
          <div className="sf-stat">
            <div className="sf-stat-value">{usageCount.toLocaleString()}</div>
            <div className="sf-stat-label">Uses</div>
          </div>
          
          <div className="sf-stat">
            <div className="sf-stat-value">{likes.toLocaleString()}</div>
            <div className="sf-stat-label">Likes</div>
          </div>

          {reputation && (
            <div className="sf-reputation">
              <ReputationBadge reputation={reputation} />
            </div>
          )}
        </div>

        <div className="sf-actions">
          <button 
            className={`sf-btn sf-btn-like ${hasLiked ? 'liked' : ''}`}
            onClick={handleLike}
            disabled={hasLiked || liking}
          >
            {hasLiked ? '❤️ Liked' : '🤍 Love this style'}
          </button>

          <button 
            className="sf-btn-report"
            onClick={handleReport}
            disabled={reporting}
          >
            ⚠️ Report issue
          </button>
        </div>
      </div>
    </>
  );
}

function ReputationBadge({ reputation }) {
  if (!reputation || typeof reputation.score !== 'number') return null;

  const { score, level } = reputation;
  
  const levelConfig = {
    emerging: { label: "Emerging", color: "#10B981", icon: "🌱" },
    established: { label: "Established", color: "#3B82F6", icon: "⭐" },
    verified: { label: "Verified", color: "#8B5CF6", icon: "✓" },
  };

  const config = levelConfig[level] || levelConfig.emerging;

  return (
    <div 
      className="reputation-badge"
      style={{ borderColor: config.color }}
      title={`Reputation: ${score}/100`}
    >
      <span className="reputation-icon">{config.icon}</span>
      <span className="reputation-label" style={{ color: config.color }}>
        {config.label}
      </span>
    </div>
  );
}

function StyleFeedbackTag() {
  return (
    <style>{`
.style-feedback {
  background: linear-gradient(135deg, #F3F0FF 0%, #FAF5FF 100%);
  border: 1px solid #E9D5FF;
  border-radius: 16px;
  padding: 16px;
  display: grid;
  gap: 12px;
}

.sf-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.sf-title {
  font-size: 14px;
  font-weight: 800;
  color: #1F2937;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.sf-style-name {
  background: #6D5EF9;
  color: #fff;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.sf-message {
  color: #1F2937;
  font-size: 14px;
  line-height: 1.5;
}

.sf-message strong {
  color: #6D5EF9;
  font-weight: 800;
}

.sf-stats-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px 0;
  border-top: 1px solid #E9D5FF;
  border-bottom: 1px solid #E9D5FF;
}

.sf-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sf-stat-value {
  font-size: 20px;
  font-weight: 800;
  color: #6D5EF9;
  line-height: 1;
}

.sf-stat-label {
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.sf-reputation {
  margin-left: auto;
}

.reputation-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: #FFFFFF;
  border: 1.5px solid;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.2s ease;
  cursor: help;
}

.reputation-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.reputation-icon {
  font-size: 12px;
  line-height: 1;
}

.reputation-label {
  line-height: 1;
}

.sf-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.sf-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  border: 2px solid #6D5EF9;
  background: #fff;
  color: #6D5EF9;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sf-btn:hover:not(:disabled) {
  background: #6D5EF9;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(109, 94, 249, 0.3);
}

.sf-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.sf-btn.liked {
  background: #6D5EF9;
  color: #fff;
  border-color: #6D5EF9;
}

.sf-btn-report {
  height: 40px;
  padding: 0 12px;
  border: none;
  background: transparent;
  color: #6B7280;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
}

.sf-btn-report:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  color: #DC2626;
}

.sf-btn-report:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sf-skeleton {
  height: 180px;
  background: linear-gradient(90deg, #f5f5f7, #ececf2, #f5f5f7);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 16px;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
    `}</style>
  );
}

function Skeleton() {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div
        style={{
          height: 20,
          width: 240,
          background: "var(--skeleton)",
          borderRadius: 6,
        }}
      />
      <div
        style={{
          height: 420,
          background: "var(--skeleton)",
          borderRadius: 22,
        }}
      />
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div
      style={{
        border: "1px solid #f2c2c2",
        background: "#fff7f7",
        color: "#8a1f1f",
        padding: 16,
        borderRadius: 12,
      }}
    >
      {message || "Something went wrong."}
    </div>
  );
}

function EditModal({
  prompt,
  styleId,
  aspect,
  onPromptChange,
  onStyleChange,
  onAspectChange,
  onClose,
  onApply,
  busy,
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,10,20,.35)",
        backdropFilter: "blur(2px)",
        display: "grid",
        placeItems: "center",
        zIndex: 50,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "min(820px, 96vw)",
          background: "white",
          borderRadius: 16,
          border: "1px solid var(--hairline)",
          boxShadow: "0 20px 55px rgba(0,0,0,.18)",
          padding: 16,
          display: "grid",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h3 className="h3" style={{ margin: 0 }}>
            Edit prompt & style
          </h3>
          <button className="btn btn-xs" onClick={onClose} disabled={busy}>
            Close
          </button>
        </div>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>Prompt</span>
          <textarea
            rows={4}
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            style={{
              width: "100%",
              borderRadius: 10,
              border: "1px solid var(--hairline)",
              padding: 10,
              resize: "vertical",
            }}
            placeholder="Describe your image…"
          />
        </label>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <label style={{ display: "grid", gap: 6, flex: "1 1 220px" }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>Style</span>
            <select
              value={styleId}
              onChange={(e) => onStyleChange(e.target.value)}
              style={{
                borderRadius: 10,
                border: "1px solid var(--hairline)",
                padding: "8px 10px",
                background: "white",
              }}
            >
              <option value="">None (Default)</option>
              {STYLES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.name} {s.status === "in-dev" ? "🚧" : ""}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: "grid", gap: 6, flex: "0 0 160px" }}>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>Aspect ratio</span>
            <select
              value={aspect}
              onChange={(e) => onAspectChange(e.target.value)}
              style={{
                borderRadius: 10,
                border: "1px solid var(--hairline)",
                padding: "8px 10px",
                background: "white",
              }}
            >
              <option value="1:1">1:1</option>
              <option value="16:9">16:9</option>
              <option value="9:16">9:16</option>
              <option value="4:3">4:3</option>
              <option value="3:4">3:4</option>
            </select>
          </label>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--muted)", marginRight: "auto" }}>
            Regenerating will spend 1 credit.
          </span>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onApply}
            disabled={busy}
            style={{ minWidth: 160 }}
          >
            {busy ? "Generating…" : "Apply & Re-generate"}
          </button>
        </div>
      </div>
    </div>
  );
}